import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  EmptyState,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FieldSection } from '@/components/common';
import { LoadingOverlay, ConfirmDialog } from '@/components/feedback';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import {
  useGetDeclarationEmissionCalculationsQuery,
  useUpdateDeclarationEmissionsMutation,
  useDeleteDeclarationEmissionsMutation,
} from '../api/declarationsApi';
import { buildEmissionCalculationFields } from '../constants/declarationEmissionFields';
import { EmissionCalculationsModal } from './EmissionCalculationsModal';
import styles from './DeclarationEmissionCalculationsTabView.module.scss';

// TODO: badge the values fetched from DDV once EmissionDetails flags their source.

/**
 * Props:
 *   declarationId  the declaration whose emission calculations the tab reads
 */
export const DeclarationEmissionCalculationsTabView = ({ declarationId }) => {
  const t = useTranslations();
  const { module } = useParams();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { data: emission, isLoading } = useGetDeclarationEmissionCalculationsQuery({
    id: declarationId,
  });

  const [updateEmissions, { isLoading: isSaving }] = useUpdateDeclarationEmissionsMutation();
  const [deleteEmissions] = useDeleteDeclarationEmissionsMutation();

  const emissionFields = useMemo(
    () => (emission ? buildEmissionCalculationFields(t, emission) : []),
    [t, emission],
  );

  const openForm = useCallback(() => setIsFormOpen(true), []);
  const closeForm = useCallback(() => setIsFormOpen(false), []);
  const openDelete = useCallback(() => setIsDeleteOpen(true), []);
  const closeDelete = useCallback(() => setIsDeleteOpen(false), []);

  const handleSubmit = useCallback(
    async (values) => {
      await updateEmissions({ module, id: declarationId, values }).unwrap();
      setIsFormOpen(false);
    },
    [updateEmissions, module, declarationId],
  );

  const handleDelete = useCallback(async () => {
    await deleteEmissions({ module, id: declarationId }).unwrap();
    setIsDeleteOpen(false);
  }, [deleteEmissions, module, declarationId]);

  if (isLoading || !emission) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  return (
    <>
      {/* `isEditable` turns true only once the route has recalculated, so it is the "has any" flag. */}
      {emission.isEditable ? (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <Typography
              variant={TypographyVariants.bodySm}
              weight={TypographyWeights.semibold}
              color={TypographyColors.primary}
            >
              {t('DECLARATIONS_DETAIL_CARD_EMISSION')}
            </Typography>
            <div className={styles.cardActions}>
              <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={openForm}>
                {t('COMMON_EDIT')}
              </Button>
              <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={openDelete}>
                {t('COMMON_DELETE')}
              </Button>
            </div>
          </div>
          <div className={styles.cardBody}>
            <FieldSection fields={emissionFields} />
          </div>
        </div>
      ) : (
        <EmptyState
          image={<img src={emptyBoxIllustration} alt="" width={200} height={200} />}
          title={t('DECLARATIONS_EMISSIONS_EMPTY_TITLE')}
          description={t('DECLARATIONS_EMISSIONS_EMPTY_DESCRIPTION')}
          primaryLabel={t('DECLARATIONS_EMISSIONS_ADD_TITLE')}
          onPrimaryClick={openForm}
        />
      )}

      <EmissionCalculationsModal
        open={isFormOpen}
        onClose={closeForm}
        emission={emission}
        isSaving={isSaving}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={isDeleteOpen}
        title={t('DECLARATIONS_EMISSIONS_DELETE_TITLE')}
        bodyTitle={t('DECLARATIONS_EMISSIONS_DELETE_TITLE')}
        description={t('DECLARATIONS_EMISSIONS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleDelete}
        onCancel={closeDelete}
      />
    </>
  );
};
